import React, { useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import RefundInstallmentSlip from './RefundInstallmentSlip'
import {
  Wallet,
  CheckCircle,
  Clock,
  RefreshCw,
  ChevronDown,
  Info,
  TrendingUp,
  Calendar
} from 'lucide-react'

// Utility functions
const formatCurrency = amount => {
  return typeof amount === 'number'
    ? `PKR ${amount.toLocaleString('en-IN')}`
    : 'PKR 0'
}

const formatDate = dateString => {
  if (!dateString) return '—'
  const date = new Date(dateString)
  return new Intl.DateTimeFormat('en-PK', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  }).format(date)
}

// Simplified Stat Card
const StatCard = ({ icon, title, value, colorClass, className }) => {
  return (
    <div className={`p-4 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm flex items-center justify-between ${className}`}>
      <div>
        <p className='text-xs text-slate-500 uppercase font-bold tracking-wider'>{title}</p>
        <p className='text-lg font-bold text-slate-800 dark:text-white mt-1'>{value}</p>
      </div>
      <div className={`p-3 rounded-full ${colorClass}`}>
        {icon}
      </div>
    </div>
  )
}

const RefundInstallmentsTable = ({ themeColor, activeSociety, plotDetails }) => {
  const { cancelPlot } = useSelector(state => state)
  const { loading } = cancelPlot || { loading: false }

  const rawData = cancelPlot?.refundInstallments || []
  // Depending on API response structure, adjust this:
  const refundInstallments = Array.isArray(rawData[1]) ? rawData[1] : []
  const statistics = rawData[2] || {
    totalPaid: 0,
    totalPending: 0,
    paidInstallments: 0,
    pendingInstallments: 0
  }

  const [statsVisible, setStatsVisible] = useState(true)

  const totalPaid = statistics?.totalPaid || 0
  const totalPending = statistics?.totalPending || 0
  const totalAmount = totalPaid + totalPending

  if (loading) {
    return (
      <div className='p-8 flex flex-col items-center justify-center text-slate-500 bg-white dark:bg-slate-800'>
        <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600 mb-2'></div>
        <p>Loading schedule...</p>
      </div>
    )
  }

  if (!refundInstallments || refundInstallments.length === 0) {
    return (
      <div className='p-12 flex flex-col items-center justify-center bg-white dark:bg-slate-800'>
        <div className='w-16 h-16 rounded-full bg-slate-100 dark:bg-slate-700 flex items-center justify-center mb-4'>
          <Wallet className='h-8 w-8 text-slate-400' />
        </div>
        <h3 className='text-slate-800 dark:text-white font-bold mb-1'>No Refund Installments</h3>
        <p className='text-slate-500 text-sm'>No payment schedule found for this refund.</p>
      </div>
    )
  }

  return (
    <div className='bg-white dark:bg-slate-800'>
      {/* Stats Section Toggle */}
      <div className='p-4 border-b border-slate-100 dark:border-slate-700 flex justify-between items-center bg-slate-50/50 dark:bg-slate-800/50'>
        <div className='flex items-center space-x-2 text-sm text-slate-600 dark:text-slate-400'>
          <Info size={16} />
          <span>Schedule Overview</span>
        </div>
        <button
          onClick={() => setStatsVisible(!statsVisible)}
          className='p-1 rounded hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors'
        >
          <ChevronDown size={16} className={`text-slate-500 transition-transform ${statsVisible ? 'rotate-180' : ''}`} />
        </button>
      </div>

      {/* Enhanced Stats Cards */}
      {statsVisible && (
        <div className='p-4 bg-gradient-to-br from-slate-50 to-teal-50/30 dark:from-slate-900/50 dark:to-teal-900/10 border-b border-slate-200 dark:border-slate-700'>
          <div className='grid grid-cols-1 md:grid-cols-4 gap-4'>
            <StatCard
              title='Total Refund Amount'
              value={formatCurrency(totalAmount)}
              icon={<Wallet size={20} className='text-teal-600 dark:text-teal-400' />}
              colorClass='bg-teal-100 dark:bg-teal-900/30'
              className='border-2 border-teal-200 dark:border-teal-800'
            />
            <StatCard
              title='Paid Amount'
              value={formatCurrency(totalPaid)}
              icon={<CheckCircle size={20} className='text-emerald-600 dark:text-emerald-400' />}
              colorClass='bg-emerald-100 dark:bg-emerald-900/30'
              className='border-2 border-emerald-200 dark:border-emerald-800'
            />
            <StatCard
              title='Pending Amount'
              value={formatCurrency(totalPending)}
              icon={<Clock size={20} className='text-amber-600 dark:text-amber-400' />}
              colorClass='bg-amber-100 dark:bg-amber-900/30'
              className='border-2 border-amber-200 dark:border-amber-800'
            />
            <StatCard
              title='Progress'
              value={`${totalAmount > 0 ? Math.round((totalPaid / totalAmount) * 100) : 0}%`}
              icon={<TrendingUp size={20} className='text-blue-600 dark:text-blue-400' />}
              colorClass='bg-blue-100 dark:bg-blue-900/30'
              className='border-2 border-blue-200 dark:border-blue-800'
            />
          </div>
          {statistics && (
            <div className='mt-4 pt-4 border-t border-slate-200 dark:border-slate-700 flex items-center justify-between text-xs text-slate-600 dark:text-slate-400'>
              <div className='flex items-center gap-4'>
                <span className='flex items-center gap-1.5'>
                  <CheckCircle size={14} className='text-emerald-600' />
                  <span className='font-medium'>{statistics.paidInstallments || 0} Paid</span>
                </span>
                <span className='flex items-center gap-1.5'>
                  <Clock size={14} className='text-amber-600' />
                  <span className='font-medium'>{statistics.pendingInstallments || 0} Pending</span>
                </span>
                <span className='flex items-center gap-1.5'>
                  <Calendar size={14} className='text-slate-600' />
                  <span className='font-medium'>{refundInstallments.length} Total Installments</span>
                </span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Enhanced Table with Better Design */}
      <div className='overflow-x-auto'>
        <div className='bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden'>
          <table className='w-full text-sm'>
            <thead className='bg-gradient-to-r from-slate-50 to-teal-50/30 dark:from-slate-700 dark:to-teal-900/20 border-b border-slate-200 dark:border-slate-700'>
              <tr>
                <th className='px-6 py-4 text-left text-xs font-bold text-slate-600 dark:text-slate-300 uppercase tracking-wider'>
                  <div className='flex items-center gap-2'>
                    <span>#</span>
                  </div>
                </th>
                <th className='px-6 py-4 text-left text-xs font-bold text-slate-600 dark:text-slate-300 uppercase tracking-wider'>
                  Installment Amount
                </th>
                <th className='px-6 py-4 text-left text-xs font-bold text-slate-600 dark:text-slate-300 uppercase tracking-wider'>
                  Due Date
                </th>
                <th className='px-6 py-4 text-left text-xs font-bold text-slate-600 dark:text-slate-300 uppercase tracking-wider'>
                  Status
                </th>
                <th className='px-6 py-4 text-left text-xs font-bold text-slate-600 dark:text-slate-300 uppercase tracking-wider'>
                  Payment Date
                </th>
                <th className='px-6 py-4 text-right text-xs font-bold text-slate-600 dark:text-slate-300 uppercase tracking-wider'>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className='divide-y divide-slate-100 dark:divide-slate-700/50 bg-white dark:bg-slate-800'>
              {refundInstallments.map((installment, index) => {
                const isPaid = installment.status === 'paid' || installment.is_paid;
                const isOverdue = !isPaid && new Date(installment.due_date) < new Date();
                
                return (
                  <tr 
                    key={installment._id || index} 
                    className={`transition-all duration-150 ${
                      isPaid 
                        ? 'bg-emerald-50/30 dark:bg-emerald-900/10 hover:bg-emerald-50/50 dark:hover:bg-emerald-900/20' 
                        : isOverdue
                        ? 'bg-rose-50/30 dark:bg-rose-900/10 hover:bg-rose-50/50 dark:hover:bg-rose-900/20'
                        : 'hover:bg-slate-50 dark:hover:bg-slate-700/30'
                    }`}
                  >
                    <td className='px-6 py-4'>
                      <div className='flex items-center gap-3'>
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-xs ${
                          isPaid 
                            ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300' 
                            : 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300'
                        }`}>
                          {index + 1}
                        </div>
                      </div>
                    </td>
                    <td className='px-6 py-4'>
                      <div className='font-bold text-base text-slate-800 dark:text-white'>
                        {formatCurrency(installment.amount)}
                      </div>
                    </td>
                    <td className='px-6 py-4'>
                      <div className='flex flex-col'>
                        <span className='text-sm font-medium text-slate-700 dark:text-slate-300'>
                          {formatDate(installment.due_date)}
                        </span>
                        {isOverdue && (
                          <span className='text-xs text-rose-600 dark:text-rose-400 mt-0.5 font-medium'>
                            Overdue
                          </span>
                        )}
                      </div>
                    </td>
                    <td className='px-6 py-4'>
                      {isPaid ? (
                        <span className='inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800'>
                          <CheckCircle size={14} />
                          Paid
                        </span>
                      ) : (
                        <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold ${
                          isOverdue
                            ? 'bg-rose-100 text-rose-700 dark:bg-rose-900 dark:text-rose-300 border border-rose-200 dark:border-rose-800'
                            : 'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300 border border-amber-200 dark:border-amber-800'
                        }`}>
                          <Clock size={14} />
                          {isOverdue ? 'Overdue' : 'Pending'}
                        </span>
                      )}
                    </td>
                    <td className='px-6 py-4'>
                      {installment.payment_date ? (
                        <div className='flex flex-col'>
                          <span className='text-sm font-medium text-slate-700 dark:text-slate-300'>
                            {formatDate(installment.payment_date)}
                          </span>
                          {installment.payment_method && (
                            <span className='text-xs text-slate-500 dark:text-slate-400 mt-0.5'>
                              {installment.payment_method}
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className='text-slate-400 dark:text-slate-500 italic'>—</span>
                      )}
                    </td>
                    <td className='px-6 py-4 text-right'>
                      {isPaid ? (
                        <RefundInstallmentSlip
                          installment={installment}
                          activeSociety={activeSociety}
                          plotDetails={plotDetails}
                        />
                      ) : (
                        <span className='text-xs text-slate-400 dark:text-slate-500'>—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className='p-4 border-t border-slate-100 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-xs text-slate-400 flex justify-between items-center'>
        <span>Showing {refundInstallments.length} installments</span>
        <button className='flex items-center space-x-1 hover:text-teal-600 transition-colors'>
          <RefreshCw size={12} />
          <span>Refresh</span>
        </button>
      </div>
    </div>
  )
}

export default RefundInstallmentsTable
